/* eslint-disable simple-import-sort/imports */
/* eslint-disable @typescript-eslint/no-explicit-any */
import AutoAwesomeIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UploadFileIcon from "@mui/icons-material/CloudUploadOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { addDays, format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";

import {
	AIParseResponse,
	AvailableProduct,
	ExtractedMO,
	productionPlanApi,
} from "../../../../services/production-plan-api";
import { workOrderAttributesApi } from "../../../../services/work-order-attributes-api";
import { CreateManufacturingOrderDto } from "../utils/calculations";
import {
	NOTES_ATTR_KEY,
	NameFixes,
	PlantLine,
	UploadConflicts,
	applyNameFixes,
	attributeValuesWithNote,
	findConflicts,
	indexPlantLines,
	namesToCheck,
} from "../utils/schedule-upload";
import ScheduleImportReview from "./ScheduleImportReview";
import {
	GhostButton,
	planner,
	PrimaryButton, // eslint-disable-line sort-imports
	staffingColor,
	staffingSoft,
	StaffingState, // eslint-disable-line sort-imports
} from "../../planner-ui";

// Wilya status ramp instead of MUI's success/warning/error palette
// (design system rule 7 — no ad-hoc greens/ambers/reds).
const statusChipSx = (state: StaffingState) => ({
	...planner.type.metaStrong,
	borderRadius: `${planner.radiusSm}px`,
	border: `1px solid ${staffingColor(state)}55`,
	bgcolor: staffingSoft(state),
	color: staffingColor(state),
	"& .MuiChip-icon": { color: staffingColor(state) },
});

// Machine data in the preview table: quantities, times, confidence figures.
const monoCellSx = {
	...planner.type.meta,
	fontFamily: planner.fontMono,
	color: planner.inkSecondary,
	fontVariantNumeric: "tabular-nums" as const,
};

const dialogTextSx = { ...planner.type.body, color: planner.inkSecondary };

interface UploadProductionPlanDialogProps {
	open: boolean;
	onClose: () => void;
	onUploadSuccess: () => void;
	weekStartDate: Date;
	/** The plant's products, to match the file's product column against. */
	products: { id: number; itemNumber?: string; sku?: string; name: string }[];
	/** The plant's lines, to match the file's line / work-center column against. */
	workcenters: string[];
	/**
	 * Every plant's lines, when the company plans more than one: the file is
	 * matched against all of them and each order goes to its line's plant.
	 */
	plantLines?: PlantLine[];
	/** The plant the upload starts from. */
	locationId?: number | null;
	/** Finish the parsed rows (plant, labor standard, run time) before they are saved. */
	prepareOrders?: (orders: CreateManufacturingOrderDto[]) => CreateManufacturingOrderDto[];
}

type UploadStep = "upload" | "select-sheet" | "preview" | "conflicts" | "uploading";

const NO_FIXES: NameFixes = { lines: {}, products: {} };

// A long schedule reads into hundreds of batches; the table shows the first ones.
const PREVIEW_ROWS = 300;

const UploadProductionPlanDialog: React.FC<UploadProductionPlanDialogProps> = ({
	open,
	onClose,
	onUploadSuccess,
	weekStartDate,
	products,
	workcenters,
	plantLines,
	locationId,
	prepareOrders,
}) => {
	const [file, setFile] = useState<File | null>(null);
	const [step, setStep] = useState<UploadStep>("upload");
	const [parsing, setParsing] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	
	// AI parsing results
	const [parseResponse, setParseResponse] = useState<AIParseResponse | null>(null);
	const [selectedSheet, setSelectedSheet] = useState<string>("");
	const [extractedOrders, setExtractedOrders] = useState<ExtractedMO[]>([]);
	const [fixes, setFixes] = useState<NameFixes>(NO_FIXES);
	const [conflicts, setConflicts] = useState<UploadConflicts | null>(null);
	const companyId = useSelector((state: any) => state.user?.userData?.companyId);

	const availableProducts: AvailableProduct[] = useMemo(
		() => products.map((p: any) => ({ id: p.id, sku: p.itemNumber || p.sku || "", name: p.name || "" })),
		[products]
	);
	const scheduleImport = parseResponse?.scheduleImport ?? null;
	const plants = useMemo(() => (plantLines && plantLines.length > 0 ? indexPlantLines(plantLines) : null), [plantLines]);
	const plantNames = useMemo(
		() => (plantLines ? Array.from(new Set(plantLines.map((line) => line.locationName))).sort() : []),
		[plantLines]
	);
	const isMultiPlant = plantNames.length > 1;
	// What the file's lines are matched against: every plant's when there are several.
	const matchLines = useMemo(() => (isMultiPlant && plants ? plants.labels : workcenters), [isMultiPlant, plants, workcenters]);
	const plantOf = useCallback((label: string) => (isMultiPlant ? plants?.byLabel.get(label)?.locationName : undefined), [isMultiPlant, plants]);
	const names = useMemo(() => (scheduleImport ? namesToCheck(extractedOrders) : null), [scheduleImport, extractedOrders]);
	// Rows as the person has fixed them (a schedule upload); a plain table upload stays as read.
	const reviewedOrders = useMemo(
		() => (scheduleImport ? applyNameFixes(extractedOrders, fixes, availableProducts) : extractedOrders),
		[scheduleImport, extractedOrders, fixes, availableProducts]
	);
	const handleFixLine = useCallback((raw: string, workcenter: string | null) => {
		setFixes((current) => ({ ...current, lines: { ...current.lines, [raw]: workcenter } }));
	}, []);
	const handleFixProduct = useCallback((raw: string, productId: number | null) => {
		setFixes((current) => ({ ...current, products: { ...current.products, [raw]: productId } }));
	}, []);

	// Reset state when dialog closes
	useEffect(() => {
		if (!open) {
			setFile(null);
			setStep("upload");
			setParsing(false);
			setUploading(false);
			setError(null);
			setParseResponse(null);
			setSelectedSheet("");
			setExtractedOrders([]);
			setFixes(NO_FIXES);
			setConflicts(null);
		}
	}, [open]);

	const parseWithAI = useCallback(async (uploadedFile: File, sheet?: string) => {
		setParsing(true);
		setError(null);

		try {
			const response = await productionPlanApi.aiParse(
				uploadedFile,
				format(weekStartDate, "yyyy-MM-dd"),
				availableProducts,
				matchLines,
				sheet,
				locationId
			);

			setParseResponse(response);

			if (!response.success) {
				setError(response.error || "Failed to parse document");
				return;
			}

			// Check if sheet selection is required (multi-sheet Excel)
			if (response.requiresSheetSelection && response.availableSheets.length > 0) {
				setStep("select-sheet");
				return;
			}

			// Show preview of extracted orders
			setExtractedOrders(response.extractedOrders);
			setFixes(NO_FIXES);
			setStep("preview");
		} catch (err: any) {
			console.error("AI parsing failed:", err);
			setError(err.response?.data?.error || err.message || "The document could not be read.");
		} finally {
			setParsing(false);
		}
	}, [weekStartDate, availableProducts, matchLines, locationId]);

	const onDrop = useCallback(async (acceptedFiles: File[]) => {
		const uploadedFile = acceptedFiles[0];
		if (uploadedFile) {
			setFile(uploadedFile);
			setError(null);
			await parseWithAI(uploadedFile);
		}
	}, [parseWithAI]);

	const handleSheetSelect = async () => {
		if (!file || !selectedSheet) return;
		await parseWithAI(file, selectedSheet);
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			"text/csv": [".csv"],
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
			"application/vnd.ms-excel": [".xls"],
			"application/pdf": [".pdf"],
			"image/png": [".png"],
			"image/jpeg": [".jpg", ".jpeg"],
			"image/webp": [".webp"],
			"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
		},
		maxFiles: 1,
		disabled: parsing,
	});

	// The Notes attribute the requirement month goes into; added for the company the first time a note comes in.
	const ensureNotesAttribute = async () => {
		if (!companyId) return;
		const definitions = await workOrderAttributesApi.getByCompany(companyId, true);
		if (definitions.some((definition) => definition.attrKey === NOTES_ATTR_KEY)) return;
		await workOrderAttributesApi.create({
			companyId,
			attrKey: NOTES_ATTR_KEY,
			label: "Notes",
			dataType: "text",
			isRequired: false,
			isSearchable: true,
			displaySlot: "detail",
			sortOrder: 90,
		});
	};

	const saveOrders = async (rows: ExtractedMO[], replace: number[]) => {
		setUploading(true);
		setStep("uploading");
		setError(null);

		try {
			if (rows.some((mo) => mo.notes)) await ensureNotesAttribute();
			// Replacing: the planned orders the upload overlaps go first, a few at a time.
			for (let i = 0; i < replace.length; i += 10) {
				await Promise.all(replace.slice(i, i + 10).map((id) => productionPlanApi.delete(id)));
			}
			const orders: CreateManufacturingOrderDto[] = rows.map((mo) => ({
				productId: mo.productId,
				sku: mo.sku,
				quantity: mo.quantity || 0,
				...placeOf(mo),
				startTime: mo.startTime,
				endTime: mo.endTime || undefined,
				orderType: mo.orderType || undefined,
				attributeValues: attributeValuesWithNote(mo.notes),
			}));

			await productionPlanApi.createBatch(prepareOrders ? prepareOrders(orders) : orders);
			onUploadSuccess();
			onClose();
		} catch (err: any) {
			setError(err.response?.data?.message || "Failed to upload production plan");
			setStep("preview");
		} finally {
			setUploading(false);
		}
	};

	// The line as saved, and its plant when the upload covers several.
	const placeOf = (mo: ExtractedMO) => {
		const line = isMultiPlant ? plants?.byLabel.get(mo.workstation) : undefined;
		return line ? { workstation: line.name, locationId: line.locationId } : { workstation: mo.workstation };
	};

	const handleUpload = async () => {
		const validOrders = reviewedOrders.filter((mo) => mo.isValid);
		if (validOrders.length === 0) {
			setError("No valid job orders to upload");
			return;
		}
		if (!scheduleImport) {
			await saveOrders(validOrders, []);
			return;
		}

		// One order per line at a time: find what is already planned (and what in the file overlaps itself) first.
		setError(null);
		try {
			const starts = validOrders.map((mo) => new Date(mo.startTime).getTime());
			const ends = validOrders.map((mo) => new Date(mo.endTime || mo.startTime).getTime());
			const planned = await productionPlanApi.getByDateRange(
				addDays(new Date(Math.min(...starts)), -7).toISOString(),
				new Date(Math.max(...ends)).toISOString()
			);
			const found = findConflicts(validOrders.map((mo) => ({ ...mo, ...placeOf(mo) })), planned);
			if (found.existing.length > 0 || found.selfOverlapping.length > 0) {
				setConflicts(found);
				setStep("conflicts");
				return;
			}
			await saveOrders(validOrders, []);
		} catch (err: any) {
			setError(err.response?.data?.message || err.message || "Could not check the plan for overlaps");
		}
	};

	const handleResolve = async (replaceExisting: boolean) => {
		if (!conflicts) return;
		const validOrders = reviewedOrders.filter((mo) => mo.isValid);
		const leaveOut = new Set([...conflicts.selfOverlapping, ...(replaceExisting ? [] : conflicts.overlappingUploaded)]);
		const rows = validOrders.filter((_, index) => !leaveOut.has(index));
		await saveOrders(rows, replaceExisting ? conflicts.existing.map((order) => order.id) : []);
	};

	const handleClose = () => {
		if (!parsing && !uploading) {
			onClose();
		}
	};

	const handleBack = () => {
		setFile(null);
		setStep("upload");
		setParseResponse(null);
		setSelectedSheet("");
		setExtractedOrders([]);
		setFixes(NO_FIXES);
		setConflicts(null);
		setError(null);
	};

	const validCount = reviewedOrders.filter((mo) => mo.isValid).length;
	const invalidCount = reviewedOrders.filter((mo) => !mo.isValid).length;

	// Confidence maps onto the staffing ramp so the badge matches the rest of
	// the planning surfaces rather than MUI's palette.
	const getConfidenceState = (confidence: number): StaffingState => {
		if (confidence >= 80) return "staffed";
		if (confidence >= 50) return "partial";
		return "unstaffed";
	};

	const renderUploadStep = () => (
		<Box
			{...getRootProps()}
			sx={{
				border: "2px dashed",
				borderColor: isDragActive ? planner.accent : planner.hairlineStrong,
				borderRadius: `${planner.radius}px`,
				p: 4,
				textAlign: "center",
				cursor: parsing ? "default" : "pointer",
				bgcolor: isDragActive ? planner.accentSoft : planner.surface,
				"&:hover": { borderColor: parsing ? planner.hairlineStrong : planner.accent },
				opacity: parsing ? 0.7 : 1,
			}}
		>
			<input {...getInputProps()} />
			{parsing ? (
				<>
					<CircularProgress sx={{ mb: 2, color: planner.accent }} />
					<Typography sx={{ ...planner.type.title, color: planner.navy, mb: 1 }}>
						<AutoAwesomeIcon sx={{ mr: 1, verticalAlign: "middle", color: planner.navy }} />
						Reading your document…
					</Typography>
					<Typography sx={dialogTextSx}>
						This may take a few seconds
					</Typography>
				</>
			) : (
				<>
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: 2 }}>
						<AutoAwesomeIcon sx={{ fontSize: 48, color: planner.accent }} />
						<UploadFileIcon sx={{ fontSize: 48, color: planner.inkMuted }} />
					</Box>
					<Typography sx={{ ...planner.type.title, color: planner.navy, mb: 1 }}>
						{isDragActive ? "Drop file here" : "Upload a work plan"}
					</Typography>
					<Typography sx={{ ...dialogTextSx, mb: 2 }}>
						Drag & drop any production plan document
					</Typography>
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
						{["CSV", "Excel", "PDF", "Images", "Word"].map((type) => (
							<Chip
								key={type}
								label={type}
								size="small"
								variant="outlined"
								sx={{
									...planner.type.metaStrong,
									borderRadius: `${planner.radiusSm}px`,
									borderColor: planner.hairlineStrong,
									color: planner.inkSecondary,
								}}
							/>
						))}
					</Box>
					<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, display: "block", mt: 2 }}>
						Job orders are read from the document; you check them before anything is saved
						{isMultiPlant ? ` · lines from ${plantNames.join(" and ")} together` : ""}
					</Typography>
				</>
			)}
		</Box>
	);

	const renderSheetSelection = () => (
		<Box sx={{ textAlign: "center", py: 2 }}>
			<Typography sx={{ ...planner.type.title, color: planner.navy, mb: 1 }}>
				Select a Sheet
			</Typography>
			<Typography sx={{ ...dialogTextSx, mb: 3 }}>
				Your Excel file contains multiple sheets. Please select the one with your production plan.
			</Typography>
			<FormControl fullWidth sx={{ maxWidth: 400, mx: "auto" }}>
				<InputLabel>Sheet</InputLabel>
				<Select
					value={selectedSheet}
					label="Sheet"
					onChange={(e) => setSelectedSheet(e.target.value)}
				>
					{parseResponse?.availableSheets.map((sheet) => (
						<MenuItem key={sheet} value={sheet}>
							{sheet}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<Box sx={{ mt: 3 }}>
				<PrimaryButton
					size="small"
					onClick={handleSheetSelect}
					disabled={!selectedSheet || parsing}
					startIcon={
						parsing ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							<AutoAwesomeIcon sx={{ fontSize: 16 }} />
						)
					}
				>
					{parsing ? "Processing..." : "Analyze Sheet"}
				</PrimaryButton>
			</Box>
		</Box>
	);

	const renderPreview = () => (
		<Box>
			{/* File info */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
				<CheckCircleIcon sx={{ color: planner.staffed }} />
				<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>{file?.name}</Typography>
				<GhostButton size="small" onClick={handleBack}>
					Change File
				</GhostButton>
			</Box>

			{/* Warnings */}
			{parseResponse?.warnings && parseResponse.warnings.length > 0 && (
				<Alert
					severity="warning"
					icon={false}
					sx={{
						...planner.type.body,
						mb: 2,
						borderRadius: `${planner.radiusSm}px`,
						border: `${planner.borderWidth}px solid ${planner.partial}55`,
						bgcolor: planner.partialSoft,
						color: planner.partial,
					}}
				>
					<Typography sx={{ ...planner.type.bodyStrong, color: planner.partial }}>Warnings:</Typography>
					<ul style={{ margin: 0, paddingLeft: 20 }}>
						{parseResponse.warnings.map((warning, idx) => (
							<li key={idx}>{warning}</li>
						))}
					</ul>
				</Alert>
			)}

			{scheduleImport && names && (
				<ScheduleImportReview
					summary={scheduleImport}
					names={names}
					fixes={fixes}
					workcenters={matchLines}
					plantOf={plantOf}
					products={availableProducts}
					onFixLine={handleFixLine}
					onFixProduct={handleFixProduct}
				/>
			)}

			{/* Stats */}
			{reviewedOrders.length > 0 && (
				<Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
					<Chip
						icon={<CheckCircleIcon />}
						label={`${validCount} valid`}
						size="small"
						sx={statusChipSx("staffed")}
					/>
					{invalidCount > 0 && (
						<Chip
							icon={<ErrorOutlineIcon />}
							label={`${invalidCount} invalid`}
							size="small"
							sx={statusChipSx("unstaffed")}
						/>
					)}
					{parseResponse?.processingTimeMs && (
						<Typography sx={{ ...monoCellSx, color: planner.inkMuted }}>
							Processed in {(parseResponse.processingTimeMs / 1000).toFixed(1)}s
						</Typography>
					)}
				</Box>
			)}

			{/* Table */}
			<TableContainer
				component={Paper}
				elevation={0}
				sx={{
					maxHeight: 400,
					border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
					borderRadius: `${planner.radius}px`,
				}}
			>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow
							sx={{
								"& .MuiTableCell-root": {
									...planner.labelCaps,
									bgcolor: planner.surfaceSubtle,
									color: planner.inkSecondary,
									borderBottomColor: planner.hairline,
								},
							}}
						>
							<TableCell>Status</TableCell>
							<TableCell>Product</TableCell>
							<TableCell>Workstation</TableCell>
							{isMultiPlant && <TableCell>Plant</TableCell>}
							<TableCell align="right">Quantity</TableCell>
							<TableCell>Date/Time</TableCell>
							{scheduleImport && <TableCell>Notes</TableCell>}
							<TableCell align="center">Confidence</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{reviewedOrders.slice(0, PREVIEW_ROWS).map((mo, index) => (
							<TableRow
								key={`${mo.source ?? ""}-${mo.startTime}-${index}`}
								sx={{
									bgcolor: mo.isValid ? undefined : planner.unstaffedSoft,
									"& .MuiTableCell-root": { borderBottomColor: planner.hairline },
								}}
							>
								<TableCell>
									{mo.isValid ? (
										<Tooltip title="Valid">
											<CheckCircleIcon sx={{ color: planner.staffed }} fontSize="small" />
										</Tooltip>
									) : (
										<Tooltip title={mo.validationErrors?.join(", ") || "Invalid"}>
											<ErrorOutlineIcon sx={{ color: planner.unstaffed }} fontSize="small" />
										</Tooltip>
									)}
								</TableCell>
								<TableCell>
									<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
										{mo.productName || mo.sku || mo.rawProduct}
									</Typography>
									{mo.rawProduct && mo.productName !== mo.rawProduct && (
										<Typography sx={{ ...monoCellSx, display: "block", color: planner.inkMuted }}>
											Raw: {mo.rawProduct}
										</Typography>
									)}
									{scheduleImport && mo.sku && mo.sku !== mo.productName && (
										<Typography sx={{ ...monoCellSx, display: "block", color: planner.inkMuted }}>
											{mo.sku}
										</Typography>
									)}
									{mo.checks && mo.checks.length > 0 && (
										<Tooltip title={mo.checks.join(" ")}>
											<Typography sx={{ ...monoCellSx, display: "block", color: planner.partial }}>
												Check
											</Typography>
										</Tooltip>
									)}
								</TableCell>
								<TableCell sx={{ ...planner.type.body, color: planner.inkSecondary }}>
									{mo.workstation || mo.rawWorkstation}
									{mo.rawWorkstation && mo.workstation !== mo.rawWorkstation && (
										<Typography sx={{ ...monoCellSx, display: "block", color: planner.inkMuted }}>
											Raw: {mo.rawWorkstation}
										</Typography>
									)}
								</TableCell>
								{isMultiPlant && (
									<TableCell sx={{ ...planner.type.body, color: planner.inkSecondary }}>
										{plantOf(mo.workstation) ?? ""}
									</TableCell>
								)}
								<TableCell align="right" sx={monoCellSx}>
									{mo.quantity ?? mo.rawQuantity}
								</TableCell>
								<TableCell sx={monoCellSx}>
									{mo.startTime ? (
										<>
											{format(new Date(mo.startTime), "MMM d, h:mm a")}
											{mo.endTime && ` - ${format(new Date(mo.endTime), scheduleImport ? "MMM d, h:mm a" : "h:mm a")}`}
										</>
									) : (
										mo.rawStartTime || "-"
									)}
								</TableCell>
								{scheduleImport && (
									<TableCell sx={{ ...planner.type.meta, color: planner.inkSecondary }}>
										{mo.notes || ""}
									</TableCell>
								)}
								<TableCell align="center">
									<Chip
										label={`${mo.confidence}%`}
										size="small"
										variant="outlined"
										sx={{
											...statusChipSx(getConfidenceState(mo.confidence)),
											fontFamily: planner.fontMono,
										}}
									/>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{reviewedOrders.length > PREVIEW_ROWS && (
				<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 1 }}>
					Showing the first {PREVIEW_ROWS} of {reviewedOrders.length} rows; all valid rows are uploaded.
				</Typography>
			)}

			{/* Validation errors summary */}
			{invalidCount > 0 && (
				<Alert
					severity="info"
					icon={false}
					sx={{
						mt: 2,
						borderRadius: `${planner.radiusSm}px`,
						border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
						bgcolor: planner.unplannedSoft,
						color: planner.inkSecondary,
					}}
				>
					<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
						{invalidCount} order{invalidCount > 1 ? "s" : ""} have validation issues and will be skipped.
						Only {validCount} valid order{validCount > 1 ? "s" : ""} will be uploaded.
					</Typography>
				</Alert>
			)}
		</Box>
	);

	const renderConflicts = () => (
		<Box sx={{ py: 1 }}>
			<Typography sx={{ ...planner.type.title, color: planner.navy, mb: 1 }}>
				Some of this overlaps the plan
			</Typography>
			{conflicts && conflicts.existing.length > 0 && (
				<Typography sx={{ ...dialogTextSx, mb: 1 }}>
					{conflicts.overlappingUploaded.length} uploaded order{conflicts.overlappingUploaded.length === 1 ? "" : "s"} overlap{" "}
					{conflicts.existing.length} order{conflicts.existing.length === 1 ? "" : "s"} already planned on the same lines. A line runs one order at a
					time, so either the planned ones make way or the uploaded ones are left out.
				</Typography>
			)}
			{conflicts && conflicts.selfOverlapping.length > 0 && (
				<Typography sx={{ ...dialogTextSx, mb: 1 }}>
					{conflicts.selfOverlapping.length} uploaded order{conflicts.selfOverlapping.length === 1 ? "" : "s"} overlap an earlier one on the same
					line in the file and will be left out.
				</Typography>
			)}
		</Box>
	);

	const renderUploading = () => (
		<Box sx={{ textAlign: "center", py: 4 }}>
			<CircularProgress sx={{ mb: 2, color: planner.accent }} />
			<Typography sx={{ ...planner.type.title, color: planner.navy, mb: 1 }}>
				Uploading Work Plan...
			</Typography>
			<Typography sx={dialogTextSx}>
				Creating {validCount} job order{validCount > 1 ? "s" : ""}
			</Typography>
			<LinearProgress
				sx={{
					mt: 3,
					maxWidth: 400,
					mx: "auto",
					height: 4,
					borderRadius: `${planner.radiusXs}px`,
					bgcolor: planner.alloy,
					"& .MuiLinearProgress-bar": { bgcolor: planner.accent },
				}}
			/>
		</Box>
	);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: { borderRadius: `${planner.radius}px`, boxShadow: planner.shadowDialog },
			}}
		>
			<DialogTitle sx={{ ...planner.type.title, color: planner.navy }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<AutoAwesomeIcon sx={{ color: planner.navy }} />
					Upload work plan
				</Box>
				<IconButton
					onClick={handleClose}
					disabled={parsing || uploading}
					sx={{ position: "absolute", right: 8, top: 8, color: planner.inkMuted }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{step === "upload" && renderUploadStep()}
				{step === "select-sheet" && renderSheetSelection()}
				{step === "preview" && renderPreview()}
				{step === "conflicts" && renderConflicts()}
				{step === "uploading" && renderUploading()}

				{error && (
					<Alert
						severity="error"
						icon={false}
						sx={{
							...planner.type.body,
							mt: 2,
							borderRadius: `${planner.radiusSm}px`,
							border: `${planner.borderWidth}px solid ${planner.unstaffed}55`,
							bgcolor: planner.unstaffedSoft,
							color: planner.unstaffed,
						}}
					>
						{error}
					</Alert>
				)}
			</DialogContent>
			<DialogActions sx={{ borderTop: `${planner.borderWidth}px solid ${planner.hairline}`, px: 3, py: 2 }}>
				{step === "upload" && (
					<GhostButton size="small" onClick={handleClose} disabled={parsing}>
						Cancel
					</GhostButton>
				)}
				{step === "select-sheet" && (
					<>
						<GhostButton size="small" onClick={handleBack} disabled={parsing}>
							Back
						</GhostButton>
						<GhostButton size="small" onClick={handleClose} disabled={parsing}>
							Cancel
						</GhostButton>
					</>
				)}
				{step === "preview" && (
					<>
						<GhostButton size="small" onClick={handleBack}>
							Back
						</GhostButton>
						<GhostButton size="small" onClick={handleClose}>
							Cancel
						</GhostButton>
						<PrimaryButton
							size="small"
							onClick={handleUpload}
							disabled={validCount === 0}
							startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
						>
							Upload {validCount} Order{validCount > 1 ? "s" : ""}
						</PrimaryButton>
					</>
				)}
				{step === "conflicts" && conflicts && (
					<>
						<GhostButton size="small" onClick={() => setStep("preview")}>
							Back
						</GhostButton>
						{conflicts.existing.length > 0 && (
							<GhostButton size="small" onClick={() => handleResolve(false)}>
								Keep what is planned
							</GhostButton>
						)}
						<PrimaryButton size="small" onClick={() => handleResolve(conflicts.existing.length > 0)}>
							{conflicts.existing.length > 0
								? `Replace ${conflicts.existing.length} planned order${conflicts.existing.length === 1 ? "" : "s"}`
								: "Upload the rest"}
						</PrimaryButton>
					</>
				)}
				{step === "uploading" && (
					<GhostButton size="small" disabled>
						Uploading...
					</GhostButton>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default UploadProductionPlanDialog;
